<script lang="ts" setup>
import { GpDropdown, GpTextInput } from '@gportal/uikit';
import AsyncModal from 'gpcore-uikit/components/new/AsyncModal.vue';
import { computed } from 'vue';
import { CloudProviderType } from 'gpcore-grpc/gpcore/type/v1/cloud_provider_pb';
import useFormData from 'gpcore-uikit/composables/useFormData';
import onModalShow from 'gpcore-uikit/composables/onModalShow';
import enumToGpDropdownData from '@lib/enumToGpDropdownData';
import { useProjectStore } from '@pinia/project';
import type { CreateProjectCloudProviderResponse } from 'gpcore-grpc/gpcore/api/cloud/v1/responses_pb';
import type { ProjectCloudProvider } from 'gpcore-grpc/gpcore/api/cloud/v1/project_pb';

const props = withDefaults(
  defineProps<{
    show?: boolean;
  }>(),
  {
    show: false,
  },
);

const emit = defineEmits<{
  (e: 'confirm', cloudProvider: ProjectCloudProvider): void;
  (e: 'close'): void;
}>();

const initialValues = {
  name: '',
  type: { key: CloudProviderType.UNSPECIFIED, label: null },
  region: '',
  apiKeyId: '',
  apiKeySecret: '',
};

const { resetForm, form } = useFormData(initialValues);

const providerTypeOptions = enumToGpDropdownData(CloudProviderType);

const isValid = computed(() => {
  return (
    form.name.length >= 5 &&
    form.type.key !== CloudProviderType.UNSPECIFIED &&
    form.region !== '' &&
    form.apiKeyId !== '' &&
    form.apiKeySecret !== ''
  );
});

const projectStore = useProjectStore();
const create = () => {
  if (!isValid.value) {
    return;
  }

  return projectStore.createProjectCloudProvider({
    ...form,
    type: form.type.key,
  });
};

const onDone = (response: CreateProjectCloudProviderResponse) => {
  emit('confirm', response.cloudProvider);
};

onModalShow(props, () => {
  resetForm();
});
</script>

<template>
  <AsyncModal
    :show="props.show"
    :header-icon="['fas', 'fa-cloud']"
    header-text="Add cloud provider"
    :async-function="create"
    confirm-text="Add"
    :is-valid="isValid"
    @close="emit('close')"
    @done="onDone"
  >
    <template #content>
      <div class="tw-mb-8 tw-px-4">
        <GpTextInput
          placeholder="Free identifier for your cloud provider"
          input-id="add-name"
          name="name"
          :required="true"
          label="Name"
          v-model="form.name"
        />
        <GpDropdown
          input-id="add-type"
          name="type"
          label="Provider"
          :data="providerTypeOptions"
          v-model="form.type"
          :emit-object="true"
        />
        <GpTextInput
          placeholder="External cloud provider region (e.g. us-west-1)"
          input-id="add-region"
          name="region"
          :required="true"
          label="Region"
          v-model="form.region"
        />
        <p class="tw-mb-4 tw-mt-2">
          Make sure you are using the correct region identifier specified by your cloud provider.
          This will be checked after creating/editing the cloud provider.
        </p>
        <GpTextInput
          placeholder="Your API key ID"
          input-id="add-api-key"
          name="api-key"
          :required="true"
          label="API key ID"
          v-model="form.apiKeyId"
        />
        <GpTextInput
          placeholder="Your API key secret"
          input-id="add-api-secret"
          name="secret"
          :required="true"
          label="API key secret"
          v-model="form.apiKeySecret"
        />
        <p class="tw-mb-4 tw-mt-2">
          API key ID and secret are used to authenticate against your cloud provider. Both values
          will be stored encrypted and will never be shown on the frontend or exposed through our
          API.
        </p>
      </div>
    </template>
  </AsyncModal>
</template>
